// import packages
import React from 'react'
import { useHistory } from 'react-router-dom'

// import apis
import { apiLoadAllCards } from 'api/cards'

// import redux, selectors, actions
import { useSelector, useDispatch } from 'react-redux'
import { loadCards } from 'redux/actions/cards'

// import contexts
import { FetchContext } from 'contexts/FetchContext'

// import hooks

// import utilities

// import common elements
import { Table, Button, Form } from 'react-bootstrap'
import { PageLayout } from 'components/elements/Layout'
import { Icon } from 'components/elements/Icons'

// import components
import AuthenticatedNav from 'components/navs/AuthenticatedNav'

// import styles

// local constants

// --------------------

const AdminCards = () => {
  const { authAxios } = React.useContext(FetchContext)
  const printRequestQueueActive = process.env.PRINT_REQUEST_QUEUE_ACTIVE === 'true'

  const history = useHistory()
  const cards = useSelector(state => Object.values(state.cards))
  const cardCount = cards.length
  const cardCountByYear = cards.reduce((acc, card) => {
    const year = new Date(card.updated_at).getFullYear()
    acc[year] = (acc[year] || 0) + 1
    return acc
  }, {})
  const sortedYears = Object.keys(cardCountByYear).sort((a, b) => b - a)

  const reduxDispatch = useDispatch()

  const [sortBy, setSortBy] = React.useState('name')
  const [sortAsc, setSortAsc] = React.useState(true)
  const [searchText, setSearchText] = React.useState('')

  const [loadedCards, setLoadedCards] = React.useState(false)

  React.useEffect(() => {
    const abortController = new AbortController()

    if (!loadedCards) {
      apiLoadAllCards(authAxios, {
        signal: abortController.signal,
        successDispatch: [reduxDispatch, loadCards],
        success: response => {
          setLoadedCards(true)
        },
      })
    }
    return () => {
      abortController.abort()
    }
  }, [loadedCards, authAxios, reduxDispatch])

  React.useEffect(() => {
    setSortAsc(sortBy === 'latest' ? false : true)
  }, [sortBy])

  const [DOWN, UP] = sortAsc ? [-1, 1] : [1, -1]

  const view = card => {
    history.push(`/card/${card.id}`)
  }

  const sortTitle = () => {
    if (sortBy === 'title') {
      setSortAsc(t => !t)
    } else {
      setSortBy('title')
    }
  }
  const sortCreator = () => {
    if (sortBy === 'creator') {
      setSortAsc(t => !t)
    } else {
      setSortBy('creator')
    }
  }
  const sortCreated = () => {
    if (sortBy === 'created') {
      setSortAsc(t => !t)
    } else {
      setSortBy('created')
    }
  }
  const sortUpdated = () => {
    if (sortBy === 'updated') {
      setSortAsc(t => !t)
    } else {
      setSortBy('updated')
    }
  }

  const titleSort = (a, b) => {
    const t1 = a.title
    const t2 = b.title
    return t1 < t2 ? DOWN : t1 > t2 ? UP : 0
  }
  const creatorSort = (a, b) => {
    const c1 = a.creator_name
    const c2 = b.creator_name
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }
  const updatedSort = (a, b) => {
    const l1 = a.updated_at || ''
    const l2 = b.updated_at || ''
    return l1 < l2 ? DOWN : l1 > l2 ? UP : 0
  }
  const createdSort = (a, b) => {
    const c1 = a.created_at || ''
    const c2 = b.created_at || ''
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }

  const currentSorter = () => {
    if (sortBy === 'title') {
      return titleSort
    } else if (sortBy === 'creator') {
      return creatorSort
    } else if (sortBy === 'created') {
      return createdSort
    } else {
      return updatedSort
    }
  }

    const matchesSearch = (card) => {
    const creator = card.creator_name.toLowerCase()
    const title = card.title.toLowerCase()
    const search = searchText.toLowerCase()
    return (
      creator.includes(search) || title.includes(search)
    )
  }
  const searchCount = cards.filter(c => matchesSearch(c)).length

  const filteredCards = cards.filter(card => matchesSearch(card))

  const sortedCards = filteredCards.sort(currentSorter())

  const sortIcon = field => {
    return field === sortBy ? (
      sortAsc ? (
        <Icon spaceAfter icon='up' />
      ) : (
        <Icon spaceAfter icon='down' />
      )
    ) : null
  }

  const actions = [
    <Button
      key='reload'
      variant='outline-secondary'
      size='sm'
      disabled={!loadedCards}
      onClick={() => setLoadedCards(false)}
    >
      Reload Cards
    </Button>]

  return (
    <PageLayout title='Cards' actions={actions}>
      <AuthenticatedNav />
      <p>
        Cards by year last updated: {sortedYears.map(year => (
        <span key={year}>{year === '2025' ? '' : ', '}{year}: {cardCountByYear[year]}</span>
        ))}</p>
      <div className='d-flex justify-flex-start align-items-baseline mt-0' style={{ gap: '1rem' }}>
              <div>
      
                  <Form.Control
                    name='search'
                      type='text'
                      size='sm'
                    placeholder='Search...'
                    onChange={e => setSearchText(e.target.value)}
                    value={searchText}
                  />
      
                </div>
              <div>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  onClick={() => setSearchText('')}
                >
                  Clear Search
                </Button>
                </div>
                <div>Matches: {searchCount}/{cardCount}</div>
            </div>
      {loadedCards && (
        <Table striped bordered hover size='sm'>
          <thead>
            <tr>
              <th onClick={sortTitle}>{sortIcon('title')}Title</th>
              <th onClick={sortCreator}>{sortIcon('creator')}Creator</th>
              <th onClick={sortCreated}>{sortIcon('created')}Created at</th>
              <th onClick={sortUpdated}>{sortIcon('updated')}Updated at</th>

              {printRequestQueueActive && <th>Print Request Counts</th>}
            </tr>
          </thead>
          <tbody>
            {sortedCards.map(card => (
              <tr key={card.id}>
                <td><Button size='sm' variant='link' onClick={() => view(card)}>
                    {card.title}
                  </Button></td>
                <td>{card.creator_name}</td>
                <td><small>{card.created}</small></td>
                <td><small>{card.updated}</small></td>
                {printRequestQueueActive && <td className='text-center'>p<strong>{card.pr_pending_count}</strong>  d<strong>{card.pr_downloaded_count}</strong> | r<strong>{card.pr_ready_count}</strong> | x<strong>{card.pr_denied_count}</strong> | c<strong>{card.pr_complete_count}</strong></td>}

              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageLayout>
  )
}

export default AdminCards
